import React, { useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Divider,
  IconButton,
  Popover,
  Stack,
  TableCell,
  TableSortLabel,
  Tooltip,
  Typography,
} from '@mui/material';
import { FilterList as FilterIcon } from '@mui/icons-material';

export type SortDirection = 'asc' | 'desc';

type ExplorerColumnHeaderProps = {
  label: string;
  width?: string;
  align?: 'left' | 'right';
  /** Omit to render a plain header cell (e.g. the actions column). */
  sortable?: boolean;
  sortActive?: boolean;
  sortDirection?: SortDirection;
  onSort?: (direction?: SortDirection) => void;
  /** Labels for the explicit direction buttons inside the options popover. */
  sortAscLabel?: string;
  sortDescLabel?: string;
  /** Filter options content rendered inside the popover; omit for no filter. */
  filterContent?: React.ReactNode;
  filterActive?: boolean;
  onClearFilter?: () => void;
};

/**
 * Explorer table header cell: click the label to toggle sorting; the funnel icon opens a
 * small overlay with filter options for the column plus explicit sort-direction shortcuts.
 */
const ExplorerColumnHeader: React.FC<ExplorerColumnHeaderProps> = ({
  label,
  width,
  align = 'left',
  sortable = false,
  sortActive = false,
  sortDirection = 'asc',
  onSort,
  sortAscLabel = 'Sort A → Z',
  sortDescLabel = 'Sort Z → A',
  filterContent,
  filterActive = false,
  onClearFilter,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const close = () => setAnchorEl(null);

  return (
    <TableCell sx={{ width }} align={align} sortDirection={sortActive ? sortDirection : false}>
      <Stack
        direction="row"
        spacing={0.5}
        alignItems="center"
        justifyContent={align === 'right' ? 'flex-end' : 'flex-start'}
      >
        {sortable ? (
          <TableSortLabel
            active={sortActive}
            direction={sortActive ? sortDirection : 'asc'}
            onClick={() => onSort?.()}
            sx={{
              // Keep the sort arrow permanently visible (MUI hides it until hover/active)
              // so a single click on any column re-orders without hunting for the control.
              '& .MuiTableSortLabel-icon': { opacity: sortActive ? 1 : 0.35 },
            }}
          >
            {label}
          </TableSortLabel>
        ) : (
          label
        )}
        {filterContent && (
          <Tooltip title={`${label} options`}>
            <IconButton
              size="small"
              aria-label={`${label} filter and options`}
              onClick={(event) => setAnchorEl(event.currentTarget)}
            >
              <Badge color="primary" variant="dot" invisible={!filterActive}>
                <FilterIcon fontSize="small" />
              </Badge>
            </IconButton>
          </Tooltip>
        )}
      </Stack>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Box sx={{ p: 1.5, minWidth: 240, maxWidth: 320 }}>
          {sortable && (
            <>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                Sort
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                <Button
                  size="small"
                  variant={sortActive && sortDirection === 'asc' ? 'contained' : 'outlined'}
                  onClick={() => {
                    onSort?.('asc');
                    close();
                  }}
                >
                  {sortAscLabel}
                </Button>
                <Button
                  size="small"
                  variant={sortActive && sortDirection === 'desc' ? 'contained' : 'outlined'}
                  onClick={() => {
                    onSort?.('desc');
                    close();
                  }}
                >
                  {sortDescLabel}
                </Button>
              </Stack>
              {filterContent && <Divider sx={{ my: 1 }} />}
            </>
          )}
          {filterContent && (
            <>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                Filter
              </Typography>
              {filterContent}
              {onClearFilter && (
                <Button
                  size="small"
                  sx={{ mt: 1 }}
                  disabled={!filterActive}
                  onClick={() => {
                    onClearFilter();
                  }}
                >
                  Clear filter
                </Button>
              )}
            </>
          )}
        </Box>
      </Popover>
    </TableCell>
  );
};

export default ExplorerColumnHeader;
